import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { AuthProvider } from '@/auth/AuthContext'
import { RequireAuth } from '@/auth/RequireAuth'
import { AppShell } from '@/components/shell/AppShell'
import { ToastProvider } from '@/components/ui'
import LoginPage from '@/pages/LoginPage'
import InvitePage from '@/pages/auth/InvitePage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import DashboardPage from '@/pages/DashboardPage'
import TenantsPage from '@/pages/tenants/TenantsPage'
import NewTenantPage from '@/pages/tenants/NewTenantPage'
import TenantLayout from '@/pages/tenants/TenantLayout'
import OverviewTab from '@/pages/tenants/tabs/OverviewTab'
import SignupTab from '@/pages/tenants/tabs/SignupTab'
import SettingsTab from '@/pages/tenants/tabs/SettingsTab'
import DomainsTab from '@/pages/tenants/tabs/DomainsTab'
import PlanTab from '@/pages/tenants/tabs/PlanTab'
import AddonsTab from '@/pages/tenants/tabs/AddonsTab'
import SportsTab from '@/pages/tenants/tabs/SportsTab'
import FeesTab from '@/pages/tenants/tabs/FeesTab'
import PeopleTab from '@/pages/tenants/tabs/PeopleTab'
import ActivityTab from '@/pages/tenants/tabs/ActivityTab'
import DangerZoneTab from '@/pages/tenants/tabs/DangerZoneTab'
import EmailTab from '@/pages/tenants/tabs/EmailTab'
import UsersPage from '@/pages/UsersPage'
import UserDetailPage from '@/pages/users/UserDetailPage'
import AuditLayout from '@/pages/audit/AuditLayout'
import ChangesTab from '@/pages/audit/ChangesTab'
import LoginsTab from '@/pages/audit/LoginsTab'
import LeadsListPage from '@/pages/growth/LeadsListPage'
import SelfSignupsPage from '@/pages/growth/SelfSignupsPage'
import LeadDetailPage from '@/pages/growth/LeadDetailPage'
import LeadImportPage from '@/pages/growth/LeadImportPage'
import InquiriesListPage from '@/pages/growth/InquiriesListPage'
import InquiryDetailPage from '@/pages/growth/InquiryDetailPage'
import CampaignsListPage from '@/pages/growth/CampaignsListPage'
import CampaignDetailPage from '@/pages/growth/CampaignDetailPage'
import CampaignEditPage from '@/pages/growth/CampaignEditPage'
import TemplatesListPage from '@/pages/growth/TemplatesListPage'
import TemplateEditPage from '@/pages/growth/TemplateEditPage'
import SchedulePage from '@/pages/growth/SchedulePage'
import PreviewsListPage from '@/pages/growth/PreviewsListPage'
import PreviewDetailPage from '@/pages/growth/PreviewDetailPage'
import PreviewCreatePage from '@/pages/growth/PreviewCreatePage'
import AnalyticsPage from '@/pages/growth/AnalyticsPage'
import FinderQueuePage from '@/pages/growth/FinderQueuePage'
import FinderRunsPage, { FinderRunDetailPage } from '@/pages/growth/FinderRunsPage'
import CandidateDetailPage from '@/pages/growth/CandidateDetailPage'
import PlatformsListPage from '@/pages/growth/PlatformsListPage'
import PlatformDetailPage from '@/pages/growth/PlatformDetailPage'
import SignupDetailPage from '@/pages/signups/SignupDetailPage'
import NotFoundPage from '@/pages/NotFoundPage'

/**
 * Every route in the console. Exported separately from <App> so tests can
 * mount it inside a MemoryRouter.
 *
 *   /login
 *   /invite/:token           accept a super-admin invite (signed out; token stays in the path)
 *   /forgot-password         ask for a reset link (signed out)
 *   /reset-password          set a new password (signed out; #<token>, cleared on load)
 *   /dashboard
 *   /tenants                 the Tenants tab: customers (made in Ops, or self-serve and paid)
 *                            ?q=&status=&addon=&source=&sort=&page=&f=field:op:value (repeated)
 *   /tenants?view=signups    Signups: the self-serve leagues that never paid, on the same list
 *   /tenants?view=deleted    Deleted: signups whose league was deleted (?q=&page=)
 *   /tenants/signups/:id     a signup by its own id: its tenant's Signup tab, or the deleted tenant's page
 *                            (?tab=signups and /tenants/signups redirect to ?view=signups)
 *   /tenants/new
 *   /tenants/:id/:tab        overview | signup | settings | domains | plan | add-ons |
 *                            sports | fees | people | activity | email | danger-zone
 *   /users                   ?q=&kind=&page=
 *   /users/:id
 *   /audit/changes | /audit/logins
 *   /growth/self-signups     ?tab=emails|follow-up|automatic (default Signups), ?q=&page=, ?state=sent
 *   /growth/leads            ?q=&status=&bucket=&type=&state=&platform=&platform_id=&has_email=&sort=&page=
 *   /growth/leads/import     upload, map columns, preview, import
 *   /growth/leads/:id
 *   /growth/inquiries        ?q=&status=&topic=&page=
 *   /growth/inquiries/:id
 *   /growth/campaigns, /growth/campaigns/new, /growth/campaigns/:id, /growth/campaigns/:id/edit
 *   /growth/templates, /growth/templates/new, /growth/templates/:id
 *   /growth/schedule         ?day=YYYY-MM-DD: every email going out, a week as a list, and Sending settings (/growth/batch and /growth/sending redirect)
 *   /growth/leads/:id/preview  paste or upload, check, build
 *   /growth/previews         ?q=&status=&page=
 *   /growth/previews/:id
 *   /growth/analytics        ?days=7|30|90
 *   /growth/finder/queue     ?q=&status=&source=&has_email=&sort=&page=
 *   /growth/finder/runs[/:id]
 *   /growth/finder/candidates/:id
 *   /growth/platforms        ?kind=&bucket=&target=&public_pages=
 *   /growth/platforms/:id
 *
 * Old portal paths (/leagues, /leagues/:id, /logins, /dev-docs,
 * /architecture, /signups, /signups/:id) redirect to their replacements.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="tenants/signups" element={<Navigate to="/tenants?view=signups" replace />} />
        <Route path="tenants/signups/:id" element={<SignupDetailPage />} />
        <Route path="tenants/new" element={<NewTenantPage />} />
        <Route path="tenants/:id" element={<TenantLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OverviewTab />} />
          <Route path="signup" element={<SignupTab />} />
          <Route path="settings" element={<SettingsTab />} />
          <Route path="domains" element={<DomainsTab />} />
          <Route path="plan" element={<PlanTab />} />
          <Route path="add-ons" element={<AddonsTab />} />
          <Route path="sports" element={<SportsTab />} />
          <Route path="fees" element={<FeesTab />} />
          <Route path="people" element={<PeopleTab />} />
          <Route path="activity" element={<ActivityTab />} />
          <Route path="email" element={<EmailTab />} />
          <Route path="danger-zone" element={<DangerZoneTab />} />
          <Route path="*" element={<TenantOverviewRedirect />} />
        </Route>
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="audit" element={<AuditLayout />}>
          <Route index element={<Navigate to="changes" replace />} />
          <Route path="changes" element={<ChangesTab />} />
          <Route path="logins" element={<LoginsTab />} />
          <Route path="*" element={<Navigate to="/audit/changes" replace />} />
        </Route>
        <Route path="growth" element={<Navigate to="leads" replace />} />
        <Route path="growth/self-signups" element={<SelfSignupsPage />} />
        <Route path="growth/leads" element={<LeadsListPage />} />
        <Route path="growth/leads/import" element={<LeadImportPage />} />
        <Route path="growth/leads/:id" element={<LeadDetailPage />} />
        <Route path="growth/platforms" element={<PlatformsListPage />} />
        <Route path="growth/platforms/:id" element={<PlatformDetailPage />} />
        <Route path="growth/inquiries" element={<InquiriesListPage />} />
        <Route path="growth/inquiries/:id" element={<InquiryDetailPage />} />
        <Route path="growth/campaigns" element={<CampaignsListPage />} />
        <Route path="growth/campaigns/new" element={<CampaignEditPage />} />
        <Route path="growth/campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="growth/campaigns/:id/edit" element={<CampaignEditPage />} />
        <Route path="growth/templates" element={<TemplatesListPage />} />
        <Route path="growth/templates/new" element={<TemplateEditPage />} />
        <Route path="growth/templates/:id" element={<TemplateEditPage />} />
        <Route path="growth/schedule" element={<SchedulePage />} />
        {/* Today's batch was replaced by the Schedule (owner, November 3). */}
        <Route path="growth/batch" element={<Navigate to="/growth/schedule" replace />} />
        {/* The Sending page is gone (owner, 2026-11-03): its notice and settings are on the Schedule. */}
        <Route path="growth/sending" element={<Navigate to="/growth/schedule" replace />} />
        <Route path="growth/leads/:id/preview" element={<PreviewCreatePage />} />
        <Route path="growth/previews" element={<PreviewsListPage />} />
        <Route path="growth/previews/:id" element={<PreviewDetailPage />} />
        <Route path="growth/analytics" element={<AnalyticsPage />} />
        <Route path="growth/finder" element={<Navigate to="queue" replace />} />
        <Route path="growth/finder/queue" element={<FinderQueuePage />} />
        <Route path="growth/finder/runs" element={<FinderRunsPage />} />
        <Route path="growth/finder/runs/:id" element={<FinderRunDetailPage />} />
        <Route path="growth/finder/candidates/:id" element={<CandidateDetailPage />} />

        {/* Old portal URLs */}
        <Route path="leagues" element={<Navigate to="/tenants" replace />} />
        <Route path="leagues/:id" element={<TenantOverviewRedirect />} />
        <Route path="logins" element={<Navigate to="/audit/logins" replace />} />
        <Route path="dev-docs" element={<Navigate to="/dashboard" replace />} />
        <Route path="architecture" element={<Navigate to="/dashboard" replace />} />
        <Route path="signups" element={<SignupsListRedirect />} />
        <Route path="signups/:id" element={<SignupRedirect />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}

/** Unknown tenant tab, or an old /leagues/:id link → the tenant's Overview. */
function TenantOverviewRedirect() {
  const { id } = useParams()
  return <Navigate to={`/tenants/${id}/overview`} replace />
}

/** An old /signups link → the Signups view of the tenant list, its search kept. */
function SignupsListRedirect() {
  const { search } = useLocation()
  // Signups is a view of the tenant list now: its search carries over.
  const q = new URLSearchParams(search).get('q')
  return <Navigate to={`/tenants?view=signups${q ? `&q=${encodeURIComponent(q)}` : ''}`} replace />
}

/** An old /signups/:id link → the signup under Tenants. */
function SignupRedirect() {
  const { id } = useParams()
  return <Navigate to={`/tenants/signups/${id}`} replace />
}

/** Opt in to React Router v7 behaviour now (silences the upgrade warnings). */
export const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true } as const

export default function App() {
  return (
    <BrowserRouter future={ROUTER_FUTURE}>
      <ToastProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}
